import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import PageContent from "components/partials/page-content/page-content";
import { Typography } from "ui/typography";
import PageContentHeader from "components/partials/page-content-header/page-content-header";
import { Button } from "ui/button";
import { DataGrid, TableLoader, TableItemsNotFound } from "ui/data-grid";

import { useClients } from "hooks/api/use-clients";
import { useCurrentUser } from "hooks/api/use-current-user";

import { createClinetTableColumns } from "containers/admin/clients/clients-table-columns";
import { hasPermission } from "utilities/user";

import { Permission } from "types/auth";

import Icons from "assets";

export const ClientsPage = () => {
  const currentUser = useCurrentUser();
  const navigate = useNavigate();

  // Pagination
  const [pageIndex, setPageIndex] = useState(0);
  const [pagesAmount, setPagesAmount] = useState(0);
  const [{ pageSize = 25, totalRecords = 0 }, setAmount] = useState<{
    pageSize: number;
    totalRecords: number;
  }>({
    pageSize: 25,
    totalRecords: 0,
  });

  const { data: clientsPage, isLoading } = useClients({
    sort: "name",
    size: pageSize,
    page: pageIndex,
  });

  const clients = clientsPage?.items ?? [];

  useEffect(() => {
    if (!clientsPage) return;
    setAmount({ pageSize, totalRecords: clientsPage.totalItems });
    setPagesAmount(clientsPage.totalPages);
  }, [clientsPage, pageSize]);

  // Create table columns
  const columns = useMemo(
    () =>
      createClinetTableColumns({
        pathname: location.pathname,
        isEditEnabled: hasPermission(currentUser, Permission.PERM_CLIENT_WRITE),
      }),
    [location.pathname, navigate],
  );

  if (hasPermission(currentUser, Permission.PERM_CLIENT_USER)) {
    return null;
  }

  return (
    <PageContent>
      <div className="flex flex-col mt-8 items-stretch gap-4">
        <PageContentHeader sx={{ my: 0 }}>
          <div className="flex flex-1 items-center justify-between">
            <Typography variant="h2" sx={{ color: "secondary.main" }}>
              Clients
            </Typography>
            <Button size="large" onClick={() => navigate("new")}>
              Create
            </Button>
          </div>
        </PageContentHeader>

        <TableLoader isLoading={isLoading} fullScreen />

        {!isLoading &&
          (clients.length ? (
            <DataGrid
              columns={columns}
              data={clients}
              isLoading={false}
              manualSorting={false}
              showingRecordsIsOn={true}
              viewByAmountSelectIsOn={true}
              totalRecords={totalRecords}
              pageSize={pageSize}
              pagesAmount={pagesAmount}
              setAmount={setAmount}
              pageIndex={pageIndex}
              setPageIndex={(pageIndex) => setPageIndex(pageIndex)}
            />
          ) : (
            <TableItemsNotFound />
          ))}
      </div>
    </PageContent>
  );
};
